import { Box, IconButton, Paper, Toolbar, Typography } from '@mui/material';
import { PlayArrow as PlayArrowIcon } from '@mui/icons-material';
import _ from 'lodash';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  getPersonPolls,
  radioPollHeaders,
  shortPersonHeaders,
} from '../../apis/utilities';
import Container from '../Container';
import {
  ExportPicker,
  FilterPicker,
  Parameters,
  TablePagination,
  Table,
} from '../controls';
import { useSnackbar } from '../Snackbar';
import ChunkedDownloader from './ChunkedDownloader';
import usePaging from './usePaging';

const {
  rowsPerPageOptions,
  useReducedResourceInformation,
  events: { eventFilters: { personTrails: eventFilters = [] } = {} } = {},
} = window.config;
const { extraTrailHeaders } = window.reportHeaders;

const headers = [
  ...extraTrailHeaders,
  {
    label: 'Start Location Type',
    key: 'startLocation.type',
    type: 'text',
    filter: true,
  },
  {
    label: 'Start Location Name',
    key: 'startLocation.name',
    type: 'text',
    filter: true,
  },
  {
    label: 'End Location Type',
    key: 'endLocation.type',
    type: 'text',
    filter: true,
  },
  {
    label: 'End Location Name',
    key: 'endLocation.name',
    type: 'text',
    filter: true,
  },
  { label: 'Start Time', key: 'startTime', type: 'date', filter: false },
  { label: 'End Time', key: 'endTime', type: 'date', filter: false },
  {
    label: 'Duration (minutes)',
    key: 'durationMinutes',
    serverKey: 'durationSeconds',
    type: 'number',
    filter: false,
  },
];

function ReplayLink({ entry }) {
  const navigate = useNavigate();

  const handleViewClick = (identifier) => () => {
    navigate(`/replay/personTrails/${identifier}`);
  };

  return (
    <IconButton
      title="View"
      size="small"
      aria-label="View"
      onClick={handleViewClick(entry.identifier)}
    >
      <PlayArrowIcon />
    </IconButton>
  );
}

function DownloadPersonPollsLink({ entry }) {
  const personTrailsHeaders = [...shortPersonHeaders, ...radioPollHeaders];

  async function fetchDataToConvert() {
    const polls = await getPersonPolls(
      entry.person.radioSsi,
      entry.startTime,
      entry.endTime
    );

    const data = polls.map((trail) => ({
      ...trail,
      ...(useReducedResourceInformation
        ? { staffId: entry.person.code }
        : {
            name: entry.person.forenames + ' ' + entry.person.surname,
            personRole: entry.person.role,
            collarNumber: entry.person.collarNumber,
          }),
      radioSsi: entry.person.radioSsi,
      time: new Date(trail.time),
      longitude: trail.position ? trail.position.coordinates[0] : 0,
      latitude: trail.position ? trail.position.coordinates[1] : 0,
    }));

    return data;
  }

  return (
    <ExportPicker
      title="download"
      fetchDataToConvert={fetchDataToConvert}
      filename="Person Trail"
      headers={personTrailsHeaders}
    />
  );
}

const replayColumn = {
  label: '',
  key: 'replay',
  type: 'component',
  component: ReplayLink,
};

const downloadColumn = {
  label: '',
  key: 'download',
  type: 'component',
  component: DownloadPersonPollsLink,
};

export default function Trails() {
  const total = useSelector((state) => state.events.trails.total);
  const trails = useSelector((state) => state.events.trails.list, _.isEqual);
  const totals = useSelector((state) => state.events.trails.totals, _.isEqual);
  const isLoading = useSelector((state) => state.events.trails.isLoading);
  const error = useSelector((state) => state.events.trails.error);
  const filter = useSelector((state) => state.events.trails.filter, _.isEqual);
  const query = useSelector((state) => state.events.trails.query, _.isEqual);
  const snackbar = useSnackbar();
  const eventType = 'trails';

  const paging = usePaging({ eventType, headers });

  useEffect(() => {
    if (error) {
      snackbar.notify('error', error);
    }
  }, [error, snackbar]);

  const filename = 'Person Trails.csv';

  const csvMapping = (trail) => ({
    ...trail,
    // 'person.code': trail.person.code,
    // 'person.forenames': trail.person.forenames,
    // 'person.surname': trail.person.surname,
    // 'person.collarNumber': trail.person.collarNumber,
    // 'person.role': trail.person.role,
    // startLocationType: trail.startLocationType,
    // startLocationName: trail.startLocationName,
    // endLocationType: trail.endLocationType,
    // endLocationName: trail.endLocationName,
    startTime: new Date(trail.startTime),
    endTime: new Date(trail.endTime),
    durationMinutes: _.round(trail.durationMinutes || 0.0, 2),
  });

  //   downloadCSV(data, filename, headers);
  // }

  return (
    <Container title="Trails">
      <Parameters
        onFetch={paging.handleFetch}
        onCancel={paging.handleCancel}
        onChange={paging.handleQueryChange}
        isFetching={isLoading}
        sx={{ mt: 1, width: 264 }}
        value={query}
        person
        eventFilters={eventFilters}
      />
      <Box
        sx={{
          flex: 1,
          height: 'calc(100vh - 48px)',
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        <Helmet>
          <title>IR3 | Trails</title>
        </Helmet>
        <Toolbar variant="dense" disableGutters sx={{ p: 1, pb: 0 }}>
          <Typography sx={{ flexGrow: 1 }} variant="subtitle1">
            Trails
          </Typography>
          <FilterPicker
            headers={headers}
            data={trails}
            filter={filter}
            filterValues={filter.filterValues}
            onFilterChange={paging.onFilterChange}
          />
          <ChunkedDownloader
            eventType={eventType}
            filename={filename}
            headers={headers}
            csvMapping={csvMapping}
          />
        </Toolbar>
        <Paper sx={{ m: [0, 1, 1], minWidth: 240 }}>
          <Box sx={isLoading ? { opacity: 0.5 } : undefined}>
            <Table
              styles={{
                tableContainer: {
                  height: 'calc(100vh - 172px)',
                  overflowY: 'scroll',
                },
                table: {
                  minWidth: 750,
                },
              }}
              data={trails}
              headers={headers.concat(replayColumn).concat(downloadColumn)}
              rowsPerPage={filter.rowsPerPage}
              page={0}
              order={filter.order}
              orderBy={filter.orderBy}
              onOrderChange={paging.handleOrderChange}
              onOrderByChange={paging.handleOrderByChange}
              totals={totals}
            />
            <TablePagination
              rowsPerPageOptions={rowsPerPageOptions}
              component="div"
              count={total ?? 0}
              rowsPerPage={filter.rowsPerPage}
              page={filter.page}
              onPageChange={paging.handlePageChange}
              onRowsPerPageChange={paging.handleRowsPerPageChange}
            />
          </Box>
        </Paper>
      </Box>
    </Container>
  );
}
